import { createApi } from '@reduxjs/toolkit/query/react';
import { CoreType, QualityControlType } from '@types';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';
import { CoreApi } from './coreApi';
import { getFileNameWithoutExtension } from '@/utils/utilFunctions';

export type args = {
  postEvents: ApiType<QualityControlType.PostEvents>['args'];
  getEventsAll: ApiType<QualityControlType.GetEventsAll>['args'];
  getEventById: ApiType<QualityControlType.GetEventById>['args'];
  patchEventById: ApiType<QualityControlType.PatchEventById>['args'];
  deleteEventById: ApiType<QualityControlType.DeleteEventById>['args'];
  getEventAttachmentById: ApiType<QualityControlType.GetEventAttachmentById>['args'];
  patchEventAttachmentById: {
    body: File[];
    params: {
      id: number;
    };
  };
  deleteEventAttachmentById: ApiType<QualityControlType.DeleteEventAttachmentById>['args'];
  postCreateCloseEvent: ApiType<QualityControlType.PostCreateCloseEvent>['args'];
  patchUpdateCloseEvent: ApiType<QualityControlType.PatchUpdateCloseEvent>['args'];
};
export type responses = {
  postEvents: ApiType<QualityControlType.PostEvents>['response'];
  getEventsAll: ApiType<QualityControlType.GetEventsAll>['response'];
  getEventById: ApiType<QualityControlType.GetEventById>['response'];
  patchEventById: ApiType<QualityControlType.PatchEventById>['response'];
  deleteEventById: void;
  patchEventAttachmentById: void;
  getEventAttachmentById: ApiType<QualityControlType.GetEventAttachmentById>['response'];
  deleteEventAttachmentById: void;
  postCreateCloseEvent: ApiType<QualityControlType.PostCreateCloseEvent>['response'];
  patchUpdateCloseEvent: ApiType<QualityControlType.PatchUpdateCloseEvent>['response'];
};

const keepUnusedDataFor = 86400;

export const qualityControlApi = createApi({
  reducerPath: 'qualityControlApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['QualityControl'],
  endpoints: (builder) => ({
    postEvents: builder.mutation<responses['postEvents'], args['postEvents']>({
      query: ({ body }) => ({
        url: '/quality-control/events/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: ['QualityControl'],
    }),

    getEventsAll: builder.query<
      responses['getEventsAll'],
      args['getEventsAll']
    >({
      query: ({ query }) => ({
        url: '/quality-control/events/',
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result) =>
        _result ? [{ type: 'QualityControl', id: 'events-all' }] : [],
    }),

    getEventById: builder.query<
      responses['getEventById'],
      args['getEventById']
    >({
      query: ({ params }) => ({
        url: `/quality-control/events/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'QualityControl', id: 'event' },
              { type: 'QualityControl', id: `event-${_arg.params.id}` },
            ]
          : [],
    }),

    patchEventById: builder.mutation<
      responses['patchEventById'],
      args['patchEventById']
    >({
      query: ({ params, body }) => ({
        url: `/quality-control/events/${params.id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'QualityControl', id: 'events-all' },
              { type: 'QualityControl', id: `event-${_arg.params.id}` },
            ]
          : [],
    }),

    deleteEventById: builder.mutation<
      responses['deleteEventById'],
      args['deleteEventById']
    >({
      query: ({ params }) => ({
        url: `/quality-control/events/${params.id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'QualityControl', id: 'events-all' },
        { type: 'QualityControl', id: `event-${_arg.params.id}` },
      ],
    }),

    getEventAttachmentById: builder.query<
      responses['getEventAttachmentById'],
      args['getEventAttachmentById']
    >({
      query: ({ params }) => ({
        url: `/quality-control/events/attachments/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'QualityControl',
                id: `event-attachment-${_arg.params.id}`,
              },
            ]
          : [],
    }),

    patchEventAttachmentById: builder.mutation<
      responses['patchEventAttachmentById'],
      args['patchEventAttachmentById']
    >({
      async queryFn(arg, api, _extraOptions, _baseQuery) {
        try {
          const files = arg?.body;
          if (!files.length) return { data: undefined };

          const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
            files?.map((file) => ({
              module: 'quality_control',
              filename: getFileNameWithoutExtension(file.name),
              content_type: file.type,
              size: file.size,
            }));

          const getPresignedUrls = await api.dispatch(
            CoreApi.Api.endpoints.postAttachmentsPresignedUrls.initiate(
              { body: getPresignedUrlsPayload },
              { track: false },
            ),
          );

          const uploadResponse = getPresignedUrls?.data?.map(async (item) => {
            const file = files?.find(
              (f) => getFileNameWithoutExtension(f.name) === item.filename,
            );

            if (!file) throw new Error(`File not found: ${item.filename}`);

            const res = await fetch(item.upload_url, {
              method: 'PUT',
              headers: { 'Content-Type': file.type },
              body: file,
            });

            if (!res.ok) throw new Error(`Upload failed for ${item.filename}`);

            return {
              filename: item.filename,
              file: item.file_url,
            };
          });

          if (!uploadResponse?.length) return { data: undefined };

          const uploadResults = await Promise.allSettled(uploadResponse);

          const successfullyUploadedFiles = uploadResults
            ?.filter((r) => r.status === 'fulfilled')
            .map((r) => r.value);

          if (!successfullyUploadedFiles?.length) return { data: undefined };

          const patchResult: responses['patchEventById'] = await api
            .dispatch(
              QualityControlApi.Api.endpoints.patchEventById.initiate(
                {
                  params: {
                    id: arg.params.id,
                  },
                  body: {
                    attachments: successfullyUploadedFiles?.map((item) => ({
                      filename: item.filename,
                      file_url: item.file,
                    })),
                  },
                },
                { track: false },
              ),
            )
            .unwrap();

          if (!patchResult) {
            return {
              error: {
                status: 400,
                data: 'Failed to upload files',
              },
            };
          }
        } catch {
          throw new Error('Failed to upload files');
        }

        return { data: undefined };
      },
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'QualityControl',
          id: `event-attachment-${_arg.params.id}`,
        },
      ],
    }),

    deleteEventAttachmentById: builder.mutation<
      responses['deleteEventAttachmentById'],
      args['deleteEventAttachmentById']
    >({
      query: ({ params }) => ({
        url: `/quality-control/events/attachments/${params.id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'QualityControl', id: 'event' },
        {
          type: 'QualityControl',
          id: `event-attachment-${_arg.params.id}`,
        },
      ],
    }),

    postCreateCloseEvent: builder.mutation<
      responses['postCreateCloseEvent'],
      args['postCreateCloseEvent']
    >({
      query: ({ body }) => ({
        url: '/quality-control/events/create-close/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['QualityControl'],
    }),

    patchUpdateCloseEvent: builder.mutation<
      responses['patchUpdateCloseEvent'],
      args['patchUpdateCloseEvent']
    >({
      query: ({ body, params }) => ({
        url: `/quality-control/events/${params.id}/update-close/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['QualityControl'],
    }),
  }),
});

export const {
  usePostEventsMutation,
  useGetEventsAllQuery,
  useGetEventByIdQuery,
  usePatchEventByIdMutation,
  useDeleteEventByIdMutation,
  useGetEventAttachmentByIdQuery,
  useDeleteEventAttachmentByIdMutation,
  usePostCreateCloseEventMutation,
  usePatchUpdateCloseEventMutation,
  usePatchEventAttachmentByIdMutation,
} = qualityControlApi;

export namespace QualityControlApi {
  export const Api = qualityControlApi;
  export type Args = args;
  export type Responses = responses;
}
